import { Banknote, HandCoins, SearchCheck, AlertTriangle, Quote } from "lucide-react";

// Plain-English explainer for factoring & broker vetting (guide Step/Mission 10).
export default function FactoringExplainer() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8" data-testid="factoring-explainer">
      <div className="flex items-center gap-2 mb-3">
        <Banknote className="w-4 h-4 text-brand" />
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Plain-English explainer</span>
      </div>
      <h3 className="font-heading text-2xl font-black tracking-tight text-slate-900">Factoring &amp; vetting brokers</h3>
      <p className="mt-3 text-slate-700 leading-relaxed">
        Brokers usually pay in <strong>30+ days</strong> — and some don't pay at all. Two moves protect a new carrier's
        cash flow: <strong>factoring</strong> your invoices and <strong>vetting</strong> every broker before you haul.
      </p>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <HandCoins className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">What is factoring?</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            A factoring company buys your freight invoice and pays you most of it (often 95–97%) within a day, then
            collects from the broker. The fee is the cost of steady cash flow. <strong>Non-recourse</strong> factoring
            protects you if the broker goes bankrupt; recourse doesn't.
          </p>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <SearchCheck className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">Vetting a broker</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Before you haul, check the broker's authority/status, bond and <strong>days-to-pay history</strong>.
            Hauling for an unvetted broker who pays in 45 days — or never — while your truck payment is due is the
            classic trap.
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-3 rounded-xl bg-white border-l-4 border-amber-500 p-5">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900 leading-relaxed">
          Plenty of carriers are profitable on paper but shut down anyway, because the money arrives too late to cover
          the bills due <strong>right now</strong>. Cash-flow timing kills more new carriers than low rates do.
        </p>
      </div>

      <div className="mt-5 rounded-xl bg-brand/5 border border-brand/20 p-5">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-7 h-7 rounded-full bg-brand flex items-center justify-center shrink-0">
            <Quote className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">Mentor tip</span>
        </div>
        <p className="text-sm text-slate-800 leading-relaxed">
          Open a factoring account before your first load, keep a cash reserve for slow weeks, and track days-to-pay by
          broker — then drop the slow ones. This isn't financial advice.
        </p>
      </div>
    </div>
  );
}
